import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { AddRounded } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { useDialog } from "../../../DialogContext";
import {
  getActivities,
  getCategories,
  updateActivity,
} from "../../../../redux/Apps/Lawyerapp/ActivitySlice/ActivitySlice";
import { backend_api } from "../../../../static/server";
import { getCases } from "../../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import {
  ListboxComponent,
  NoDataOptionField,
} from "../../../../static/staticdata";
import ExpCategoriForm from "./ExpCategoriForm";
import noOption from "../../../../assets/no-option-found.webp";
import { MatterApiField } from "../../Settings/FieldsUi/InputFields";
import DrawerTemplate from "../../Cases/DrawerTemplate";

const ExpenseForm = ({
  entryData,
  editMode,
  defaultCaseId = null,
  defaultCaseData = null,
  disableCase = false,
  defaultCategoryId = null,
  disableCategory = false,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showDialog, closeDialog } = useDialog();
  const [isEditMode, setIsEditMode] = useState(editMode ? editMode : false);
  const { token } = useSelector((data) => data.user);
  const { allCategories, loading } = useSelector((state) => state.activity);
  console.log(defaultCategoryId);

  useEffect(() => {
    dispatch(
      getCategories({
        url: `${backend_api}firm-case/expense-category/`,
        token: token,
      })
    );
    dispatch(getCases({ url: `${backend_api}case/register/`, token: token }));
  }, [dispatch, token]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      type: "expense",
      case: defaultCaseId,
      category: defaultCategoryId,
      quantity: 1,
      hourly_rate: 0,
      description: "",
      non_billable: false,
      bill_entry_show: false,
    },
  });

  useEffect(() => {
    if (defaultCaseId && !entryData) {
      setValue("case", defaultCaseId);
    }
  }, [defaultCaseId, entryData, setValue]);
  useEffect(() => {
    if (defaultCaseData && !entryData) {
      setValue("case", defaultCaseData.id);
    }
  }, [defaultCaseData, entryData, setValue]);
  useEffect(() => {
    if (defaultCategoryId && !entryData) {
      setValue("category", defaultCategoryId);
    }
  }, [defaultCategoryId, entryData, setValue]);
  useEffect(() => {
    if (entryData && isEditMode && editMode) {
      Object.keys(entryData)?.forEach((key) => {
        setValue(key, entryData[key], { shouldDirty: true });
      });
    }
  }, [entryData, isEditMode, setValue, editMode]);

  const onSubmit = async (data) => {
    try {
      let responseData = null;
      if (isEditMode) {
        responseData = await dispatch(
          updateActivity({
            url: `${backend_api}firm-case/expense/${entryData?.id}/`,
            data: data,
            token: token,
          })
        ).unwrap();
        toast.success("Expense entry updated successfully");
        setIsEditMode(false);
      } else {
        const response = await axios.post(
          `${backend_api}firm-case/expense/`,
          data,
          {
            headers: {
              Authorization: `Token ${token}`,
            },
          }
        );
        responseData = response.data;
        toast.success("Expense entry created successfully");
      }

      if (onSuccess && responseData) onSuccess(responseData);
    } catch (error) {
      toast.error(error.message || "Failed to create expense");
    } finally {
      reset();
      closeDialog();
      dispatch(
        getActivities({ url: `${backend_api}firm-case/expense/`, token: token })
      );
    }
  };

  const quantity = watch("quantity") || 0;
  const price = watch("hourly_rate") || 0;
  const non_billable = watch("non_billable");

  // Calculate total amount
  const totalAmount = useMemo(() => {
    if (non_billable) return 0;
    return (parseFloat(quantity) * parseFloat(price)).toFixed(2);
  }, [quantity, price, non_billable]);

  return (
    <>
      <Box sx={{ position: "relative" }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              p: "1rem",
            }}
          >
            <Box>
              <Controller
                name="category"
                control={control}
                rules={{ required: "Category is required" }}
                render={({ field }) => (
                  <FormControl
                    size="small"
                    fullWidth
                    error={!!errors?.category}
                  >
                    <Autocomplete
                      {...field}
                      options={allCategories || []}
                      loading={loading}
                      disabled={disableCategory}
                      getOptionLabel={(option) => {
                        if (!option) return "";
                        return option.name || "";
                      }}
                      value={
                        field.value
                          ? allCategories?.find(
                              (item) => item.id === field.value
                            ) || null
                          : null
                      }
                      onChange={(_, newValue) =>
                        field.onChange(newValue?.id || null)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={
                            <span>
                              Category<span style={{ color: "red" }}>*</span>
                            </span>
                          }
                          size="small"
                          error={!!errors?.category}
                          helperText={errors?.category?.message}
                        />
                      )}
                      ListboxComponent={(props) => (
                        <ListboxComponent
                          {...props}
                          btnIcon={<AddRounded />}
                          btnText="Add category"
                          handleClikListBtn={() => {
                            showDialog(<ExpCategoriForm />, {
                              dialogTitle: "New Expense category",
                            });
                          }}
                        />
                      )}
                      noOptionsText={
                        <NoDataOptionField
                          imgSrc={noOption}
                          text="No category found"
                          btnText="Category"
                          handleClickNoData={() => {
                            showDialog(<ExpCategoriForm />, {
                              dialogTitle: "New Expense category",
                            });
                          }}
                        />
                      }
                      PopperProps={{
                        sx: { zIndex: 3000 },
                        container: document.body,
                      }}
                    />
                  </FormControl>
                )}
              />
            </Box>
            <Box>
              <Controller
                name="case"
                control={control}
                rules={{ required: "Matter is required" }}
                render={({ field }) => (
                  <MatterApiField
                    label="Matter"
                    value={field.value}
                    onChange={field.onChange}
                    errors={errors}
                    token={token}
                    apiUrl={`${backend_api}case/register/`}
                    isRequired={true}
                    generateUniqueMatterName={(id, name) => `000${id}-${name}`}
                    AddMatterFunc={() =>
                      showDialog(<DrawerTemplate />, {
                        dialogTitle: "Select Template",
                      })
                    }
                    editData={defaultCaseData}
                    disabled={disableCase || isEditMode}
                  />
                )}
              />
            </Box>
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Controller
                name="quantity"
                control={control}
                defaultValue={1.0}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    label="Quantity"
                    type="number"
                    inputProps={{ step: "0.01", min: "0" }}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="hourly_rate"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    {...field}
                    label="Price"
                    type="number"
                    inputProps={{ step: "0.01", min: "0" }}
                    fullWidth
                    error={!!errors?.rate}
                    helperText={errors?.rate?.message}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                    }}
                  />
                )}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "1px solid #c5c5c7",
                borderRadius: "0.2rem",
                px: "0.5rem",
                py: "0.5rem",
                bgcolor: non_billable ? "#f5f5f5" : "#e3f2fd",
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontSize: "0.9rem", fontWeight: "500" }}
              >
                Total Amount
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1rem",
                  fontWeight: "600",
                  color: non_billable ? "#9e9e9e" : "primary.main",
                }}
              >
                ${non_billable ? "0.00" : totalAmount}
              </Typography>
            </Box>
            <Controller
              name="description"
              control={control}
              defaultValue=""
              rules={{ required: "Description is required" }}
              render={({ field }) => (
                <TextField
                  size="small"
                  {...field}
                  label={
                    <span>
                      Description<span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  multiline
                  rows={3}
                  fullWidth
                  error={!!errors?.description}
                  helperText={errors?.description?.message}
                />
              )}
            />
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Controller
                name="non_billable"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          if (e.target.checked) {
                            setValue("bill_entry_show", false);
                          }
                        }}
                      />
                    }
                    label="Non-Billable"
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        color: "text.secondary",
                      },
                    }}
                  />
                )}
              />
              <Controller
                name="bill_entry_show"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={!watch("non_billable")}
                      />
                    }
                    label="Show Entry on Bill"
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        color: "text.secondary",
                      },
                    }}
                  />
                )}
              />
            </Box>
          </Box>
          <Box
            sx={{
              height: "fit-content",
              borderTop: "1px solid #ddd",
              position: "sticky",
              bottom: 0,
              backdropFilter: "blur(10px)",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              p: "0.5rem",
              zIndex: 1000,
              gap: "1rem",
            }}
          >
            <Button variant="outlined" size="small" onClick={closeDialog}>
              Cancel
            </Button>
            <LoadingButton
              // loading={isSubmiting}
              variant="contained"
              color="primary"
              type="submit"
              size="small"
            >
              {isEditMode ? "Edit Expense" : "Save Expense"}
            </LoadingButton>
          </Box>
        </form>
      </Box>
    </>
  );
};

export default ExpenseForm;
